import { FC, useState, FormEvent, useEffect, useRef } from "react";
import { View, useClientContext } from "@data-root/react";
import { LoginOutlined } from "@ant-design/icons";
import { Input } from "antd";
import { UserOutlined as UserIcon, LockOutlined as LockIcon } from "@ant-design/icons";
import { stylesheet } from ".";
import { useAsyncCallback, CustomButton } from "@data-root/react";
import { useNavigate, useSearchParams } from "react-router";
import { App as AntdApp, Typography } from "antd";
import { Logo } from "@/components/logo";
import { AuthWrapper } from "@/components/auth-wrapper";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

/*
  TODO:
  - [ ] Pending logo
  - [ ] Pending functionality to Remember me, Forgot password?, Sign up
  - [ ] Pending catch error for the invalid pwd instead of falling to ErrorBoundary
*/
export const LogInScreen: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const { login } = useClientContext();
  const antdApp = AntdApp.useApp();

  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const isSubmittingRef = useRef(false);
  const autofillDetectedRef = useRef(false);

  const onSubmitLogin = useAsyncCallback(
    async (e?: FormEvent) => {
      if (e) {
        e.preventDefault();
      }

      // Prevent duplicate submissions
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;

      // For autofill: read values directly from DOM if state is empty
      const emailInput = document.getElementById("username") as HTMLInputElement;
      const passwordInput = document.getElementById("password") as HTMLInputElement;

      const emailValue = email || emailInput?.value || "";
      const passwordValue = password || passwordInput?.value || "";

      // Update state if we're using DOM values
      if (!email && emailValue) setEmail(emailValue);
      if (!password && passwordValue) setPassword(passwordValue);

      const success = await login(emailValue, passwordValue);

      if (!success) {
        isSubmittingRef.current = false;
        return antdApp.notification.error({
          key: "login",
          message: t("login_failed", { defaultValue: "Login failed" }),
          description: t("login_failed_description", { defaultValue: "Please verify your credentials." }),
        });
      }

      if (searchParams.has("next")) navigate(searchParams.get("next")!);
      else navigate("/library");
    },
    [antdApp.notification, login, email, password, searchParams, navigate, t]
  );

  // Detect autofill and auto-submit
  useEffect(() => {
    // Method 1: CSS animation trick to detect autofill
    const style = document.createElement("style");
    style.textContent = `
      @keyframes onAutoFillStart { from {/**/} to {/**/} }
      @keyframes onAutoFillCancel { from {/**/} to {/**/} }
      input:-webkit-autofill { animation: onAutoFillStart 0ms; }
      input:not(:-webkit-autofill) { animation: onAutoFillCancel 0ms; }
    `;
    document.head.appendChild(style);

    const handleAnimationStart = (e: AnimationEvent) => {
      if (e.animationName === "onAutoFillStart") {
        autofillDetectedRef.current = true;
        // Check if both fields have values after a longer delay for Safari
        setTimeout(() => {
          const emailInput = document.getElementById("username") as HTMLInputElement;
          const passwordInput = document.getElementById("password") as HTMLInputElement;

          if (emailInput?.value && passwordInput?.value && !isSubmittingRef.current) {
            // Update React state from DOM values first
            setEmail(emailInput.value);
            setPassword(passwordInput.value);
            // Auto-submit the form after state is updated
            setTimeout(() => {
              onSubmitLogin();
            }, 50);
          }
        }, 300); // Increased delay for Safari to properly fill fields
      }
    };

    // Method 2: Poll for value changes (fallback for browsers that don't fire events)
    let pollCount = 0;
    const pollInterval = setInterval(() => {
      const emailInput = document.getElementById("username") as HTMLInputElement;
      const passwordInput = document.getElementById("password") as HTMLInputElement;

      if (emailInput?.value && passwordInput?.value && !email && !password && !isSubmittingRef.current) {
        // Values appeared without user typing - likely autofill
        setEmail(emailInput.value);
        setPassword(passwordInput.value);
        autofillDetectedRef.current = true;
        setTimeout(() => {
          if (!isSubmittingRef.current) {
            onSubmitLogin();
          }
        }, 350); // Match the delay with animation method
      }

      // Stop polling after 5 seconds
      pollCount++;
      if (pollCount > 50) {
        clearInterval(pollInterval);
      }
    }, 100);

    // Add animation event listeners
    document.addEventListener("animationstart", handleAnimationStart, true);

    return () => {
      document.removeEventListener("animationstart", handleAnimationStart, true);
      document.head.removeChild(style);
      clearInterval(pollInterval);
    };
  }, [email, password, onSubmitLogin]);

  // Form with proper autofill attributes for biometric password managers
  return (
    <AuthWrapper>
      <div className="flex flex-col items-center space-y-6 relative">
        <Logo size={64} />
        <form ref={formRef} onSubmit={onSubmitLogin} autoComplete="on" className="w-full">
          <div className="w-full space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium mb-2 text-foreground">
                {t("email_or_username", { defaultValue: "Email or Username" })}
              </label>
              <Input
                id="username"
                name="username"
                size="large"
                type="text"
                value={email}
                prefix={<UserIcon className="text-muted-foreground" />}
                className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-200"
                placeholder={t("email_placeholder", { defaultValue: "user@example.com" })}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2 text-foreground">
                {t("password", { defaultValue: "Password" })}
              </label>

              <Input.Password
                id="password"
                name="password"
                size="large"
                value={password}
                prefix={<LockIcon className="text-muted-foreground" />}
                className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-200"
                placeholder={t("password_placeholder", { defaultValue: "Enter your password" })}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
              />
            </div>
          </div>

          <CustomButton
            block
            type="primary"
            htmlType="submit"
            icon={<LoginOutlined />}
            className="h-11 mt-6 bg-primary hover:bg-primary/90 dark:hover:bg-primary/80 text-primary-foreground font-medium transition-all duration-200 hover:shadow-lg"
          >
            {t("sign_in", { defaultValue: "Sign In" })}
          </CustomButton>
        </form>
      </div>
    </AuthWrapper>
  );
};
